import React, { useState, useEffect, useRef } from "react"
import { Container, Row, Col, Alert } from "react-bootstrap"
import { Switch, Route, useHistory, useRouteMatch } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Login, ForgotPassword, Register, ResetPassword } from "./views"
import { useLogin, useRegister, useRequestResetEmail, useResetPassword } from "../../hooks/useAuthMutations"
import { getAccessToken } from "../../storage/authStorage"
import LanguagePicker from "../../components/languagePicker"

import bg1 from "../../assets/login/bg-cody-chan.jpg"
import bg2 from "../../assets/login/bg-narahari-kr.jpg"
import bg3 from "../../assets/login/bg-saikrishna-sy.jpg"
import logo from "../../assets/gettips_logo_purple.svg"

interface Notification {
  type: string
  title: string
  text: string
}

interface BackgroundImage {
  src: string
  credit: string
  link: string
  alt: string
}

const NOTIFICATIONS: Record<string, Notification> = {
  registerSuccess: {
    type: "success",
    title: "auth.notifications.register.success.title",
    text: "auth.notifications.register.success.text"
  },
  registerFailed: {
    type: "danger",
    title: "auth.notifications.register.failed.title",
    text: "auth.notifications.register.failed.text"
  },
  loginFailed: {
    type: "danger",
    title: "auth.notifications.login.failed.title",
    text: "auth.notifications.login.failed.text"
  },
  requestSuccess: {
    type: "success",
    title: "auth.notifications.requestReset.success.title",
    text: "auth.notifications.requestReset.success.text"
  },
  changePasswordSuccess: {
    type: "success",
    title: "auth.notifications.resetPassword.success.title",
    text: "auth.notifications.resetPassword.success.text"
  },
  changePasswordFailed: {
    type: "danger",
    title: "auth.notifications.resetPassword.failed.title",
    text: "auth.notifications.resetPassword.failed.text"
  }
}

const BACKGROUND_DELAY = 8000

const BACKGROUND_IMAGES: BackgroundImage[] = [
  {
    src: bg1,
    credit: "Photo by Cody Chan",
    link: "https://unsplash.com/@cceee",
    alt: "Summer bench on the beach house next to ocean"
  },
  {
    src: bg2,
    credit: "Photo by Narahari K R",
    link: "https://unsplash.com/@naaaaraaaaa",
    alt: "Finland Nuuksio national park"
  },
  {
    src: bg3,
    credit: "Photo by SaiKrishna Saketh Yellapragada",
    link: "https://unsplash.com/@saiksaketh",
    alt: "Finland national park with boat and water"
  }
]

const AuthContainer = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const resetPasswordMatch = useRouteMatch<{ token: string }>("/reset-password/:token")

  const loginMutation = useLogin()
  const registerMutation = useRegister()
  const requestResetEmailMutation = useRequestResetEmail()
  const resetPasswordMutation = useResetPassword()

  const [notification, setNotification] = useState<Notification | null>(null)
  const [imageIndex, setImageIndex] = useState(0)
  const [values, setValues] = useState<{ email?: string } | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const isLoading =
    loginMutation.isPending ||
    registerMutation.isPending ||
    requestResetEmailMutation.isPending ||
    resetPasswordMutation.isPending

  useEffect(() => {
    const token = getAccessToken()
    if (token) {
      history.push("/")
    }

    intervalRef.current = setInterval(() => {
      setImageIndex(prev => (prev < BACKGROUND_IMAGES.length - 1 ? prev + 1 : 0))
    }, BACKGROUND_DELAY)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [history])

  const onSubmitLogin = (loginValues: { email: string; password: string }) => {
    if (loginValues.email && loginValues.password) {
      loginMutation
        .mutateAsync({ email: loginValues.email, password: loginValues.password })
        .then(() => history.push("/"))
        .catch(() => setNotification(NOTIFICATIONS.loginFailed))
    }
  }

  const onSubmitRegister = (registerValues: { firstName: string; lastName: string; email: string; password: string; promocode: string; terms: boolean }) => {
    const { language } = i18n

    if (registerValues && registerValues.email) {
      registerMutation
        .mutateAsync({ ...registerValues, language })
        .then(() => {
          history.push({ pathname: "/login" })
          setNotification(NOTIFICATIONS.registerSuccess)
          setValues(registerValues)
        })
        .catch(() => {
          history.push({ pathname: "/login" })
          setNotification(NOTIFICATIONS.registerFailed)
        })
    }
  }

  const onSubmitRequest = (email: string) => {
    const { language } = i18n

    if (email) {
      requestResetEmailMutation
        .mutateAsync({ email, language })
        .then(() => {
          history.push({ pathname: "/login" })
          setNotification(NOTIFICATIONS.requestSuccess)
        })
        .catch(() => {})
    }
  }

  const onSubmitResetPassword = (password: string) => {
    const token = resetPasswordMatch?.params?.token
    const { language } = i18n

    if (password && token) {
      resetPasswordMutation
        .mutateAsync({ password, token, language })
        .then(() => {
          history.push({ pathname: "/login" })
          setNotification(NOTIFICATIONS.changePasswordSuccess)
        })
        .catch(() => {
          setNotification(NOTIFICATIONS.changePasswordFailed)
        })
    } else {
      setNotification(NOTIFICATIONS.changePasswordFailed)
    }
  }

  return (
    <Container fluid className="auth h-100 align-items-center bg-white min-vh-100">
      <LanguagePicker absolute />
      <Row className="h-100">
        <Col xs={0} md={8} xl={9} className="d-none d-md-block image-container">
          {BACKGROUND_IMAGES.map((image, index) => (
            <div key={index} className={"background-image " + (imageIndex === index ? "visible" : "hidden")}>
              <img src={image.src} alt={image.alt} />
              <div className="credits">
                <a href={image.link} target="_blank" rel="noopener noreferrer">
                  {image.credit}
                </a>
              </div>
            </div>
          ))}
        </Col>
        <Col xs={12} md={4} xl={3} className="bg-light-md min-vh-100">
          <Row className="h-100 py-4 align-items-end text-center content">
            <div className="auth-form-container">
              <div className="auth-form-content mb-4">
                <div className="w-100 h-100">
                  <img src={logo} alt="Get Tips logo purple" className="logo" />
                  {notification && (
                    <Container>
                      <Row className="mb-2">
                        <Col>
                          <Alert variant={notification.type} onClose={() => setNotification(null)} dismissible>
                            <Alert.Heading>{t(notification.title)}</Alert.Heading>
                            <p className="mb-0 mr-3">{t(notification.text)}</p>
                          </Alert>
                        </Col>
                      </Row>
                    </Container>
                  )}
                  <Switch>
                    <Route path="/login">
                      <Login onSubmit={onSubmitLogin} isLoading={isLoading} values={values ?? undefined} />
                    </Route>
                    <Route path="/register">
                      <Register onSubmit={onSubmitRegister} isLoading={isLoading} />
                    </Route>
                    <Route path="/forgot-password">
                      <ForgotPassword onSubmit={onSubmitRequest} isLoading={isLoading} />
                    </Route>
                    <Route path="/reset-password/:token">
                      <ResetPassword onSubmit={onSubmitResetPassword} isLoading={isLoading} />
                    </Route>
                  </Switch>
                </div>
              </div>
            </div>
            <Col xs={12} className="pb-3 footer-links">
              <a href="https://www.gettips.fi/legal/app-terms-of-service" target="_blank" rel="noopener noreferrer">
                {t("auth.footer.termsOfService")}
              </a>
              <a href="https://www.gettips.fi/legal/app-privacy-policy" target="_blank" rel="noopener noreferrer">
                {t("auth.footer.privacyPolicy")}
              </a>
              <a href="https://www.gettips.fi/contact-us" target="_blank" rel="noopener noreferrer">
                {t("auth.footer.customerSupport")}
              </a>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  )
}

export default AuthContainer
